import { FlightCabinTypes, FlightTripTypes } from "@/types/chat"
import { DayObject, START_OF_DAY, Timestamp } from "@/types/date"
import { PlaceRef } from "@/types/entity"
import { Airline } from "@/types/flights/airline"
import { FlightSeatMapUnavailableException } from "@/types/flights/exceptions"
import { FlightBadgeContract } from "@/types/flights/flight-badge"
import { FlightBookingApi } from "@/types/flights/flight-booking-api"
import { FlightContractUpgrade } from "@/types/flights/flight-contract-upgrade"
import {
  FlightItineraryApi,
  FlightItinerarySummary,
} from "@/types/flights/flight-itinerary-api"
import { FlightLegApi, FlightLocalTimes } from "@/types/flights/flight-leg"
import {
  FlightOption,
  FlightOptionPayload,
} from "@/types/flights/flight-option"
import { FlightSearchFilters } from "@/types/flights/flight-search-filters"
import { FlightSearchInput } from "@/types/flights/flight-search-input"
import {
  FlightSearchOptions,
  FlightTravelers,
  ValidatedMultiCitySegment,
} from "@/types/flights/flight-search-options"
import { FlightSearchParams } from "@/types/flights/flight-search-params"
import {
  FlightSearchSummary,
  FlightSummaryOption,
} from "@/types/flights/flight-search-summary"
import {
  FlightSeatMap,
  FlightSeatMapValue,
  SeatSelectionStatus,
} from "@/types/flights/flight-seat-map"
import { FlightSliceApi } from "@/types/flights/flight-slice-api"
import { FlightSliceParams } from "@/types/flights/flight-slice-params"
import { FlightTransitPoint } from "@/types/flights/transit-points"
import {
  ItineraryEvent,
  ItineraryEventWithMindtripFlightBooking,
} from "@/types/itinerary"
import { AirportSummary } from "@/types/place"
import { Trip } from "@/types/trip"
import { NonNullableFields, Optional } from "@/types/util"
import last from "lodash/last"
import orderBy from "lodash/orderBy"
import sortBy from "lodash/sortBy"
import uniqBy from "lodash/uniqBy"
import pluralize from "pluralize"
import { z } from "zod"

import {
  flightItineraryApiToAmplitudeProperties,
  flightUpgradeToAmplitudeProperties,
  itineraryEventToAmplitudeProperties,
} from "@/lib/analytics/amplitude-properties"
import { sendAnalyticsEvent } from "@/lib/analytics/amplitude-utils"
import { FlightApi, SaveFlightItinerary } from "@/lib/flight-api"
import { routes } from "@/lib/routes"
import {
  FlightSearchSliceState,
  FlightTripTarget,
} from "@/lib/storage/flight-search-slice"
import { assertExists } from "@/lib/utils/assertions"
import { product } from "@/lib/utils/geo"
import { decodeUriJson, encodeUriJson } from "@/lib/utils/url-params"
import type { BadgeColor } from "@/components/ui/badge"
import {
  beforeDayObject,
  beforeTimestamp,
  browserTimeZone,
  durationInMinutes,
  getDayObject,
  getTimestamp,
  hoursBetweenDecimal,
  nightsBetweenDayObjects,
  now,
  today,
} from "./date-utils"
import {
  formatDayBoundarySuffix,
  formatFlightDuration,
  formatHourMinute,
  formatHourMinuteZone,
  formatTimeZone,
} from "./format-utils"
import { itineraryApi } from "./storage/api/itinerary-api"
import { populateTripCache } from "./storage/api/trips-api"
import { AppDispatch } from "./storage/redux-store"
import { dayRangeForTimeFrame } from "./time-frame-utils"
import { getTripTravelersOrDefault } from "./trip-utils"

export function getDistinctAirlines(slices: FlightSliceApi[]): Airline[] {
  return uniqBy(
    slices.flatMap((s) => s.flight_legs.flatMap((l) => l.properties.airlines)),
    (a) => a.iata
  )
}

/** title-case carrier suffix cleanup: Airlines, Airline, Air Lines, Airways; trailing word Air */
export function stripAirlineWord(text: string): string {
  return text
    .replace(/\bAir\s+Lines\b/g, "")
    .replace(/\bAirlines\b/g, "")
    .replace(/\bAirline\b/g, "")
    .replace(/\bAirways\b/g, "")
    .replace(/(?:^|\s)Air\s*$/g, "")
    .replace(/\s{2,}/g, " ")
    .trim()
}

export type FlightBadge = { label: string; color?: BadgeColor; key: string }

const BADGE_COLORS: Record<string, BadgeColor> = {
  best: "sky",
  cheapest: "green",
  fastest: "plum",
  best_value: "sky",
  best_schedule: "sky",
  recommended: "sky",
  nonstop: "amber",
  alternative: "gray",
  worth_considering: "gray",
  rejected: "gray",
  risky_ticketing: "amber",
}

function renderFlightBadge(badge: FlightBadgeContract): FlightBadge {
  return {
    label: badge.title,
    color: BADGE_COLORS[badge.key] ?? "gray",
    key: badge.key,
  }
}

export function buildFlightBadges(
  tag: string | undefined,
  tags: string[] | undefined,
  badges?: FlightBadgeContract[]
): FlightBadge[] {
  const source = badges?.length
    ? badges
    : [tag, ...(tags ?? [])]
        .filter((t): t is string => !!t)
        .map((raw) => ({
          title: raw,
          key: raw.trim().toLowerCase().split(/\s+/).join("_"),
        }))
  const seen = new Set<string>()
  const result: FlightBadge[] = []
  for (const sourceBadge of source) {
    const badge = renderFlightBadge(sourceBadge)
    if (seen.has(badge.key)) continue
    seen.add(badge.key)
    result.push(badge)
  }
  return result
}

/**
 * Adapter for the detail-card surface where FlightOption shape carries
 * denormalized best/cheapest/fastest booleans. Converts to the unified
 * FlightBadge[] shape so FlightOptionBadges can render.
 */
export function flightOptionToBadges(option: {
  best?: Optional<boolean>
  cheapest?: Optional<boolean>
  fastest?: Optional<boolean>
}): FlightBadge[] {
  const badges: FlightBadge[] = []
  if (option.best)
    badges.push(renderFlightBadge({ key: "best", title: "Best" }))
  if (option.cheapest) {
    badges.push(renderFlightBadge({ key: "cheapest", title: "Cheapest" }))
  }
  if (option.fastest) {
    badges.push(renderFlightBadge({ key: "fastest", title: "Fastest" }))
  }
  return badges
}

export function stopsToText(stops: number): string {
  if (stops === 0) {
    return "Nonstop"
  } else {
    return `${stops} ${pluralize("stop", stops)}`
  }
}

export function flightItineraryExpired(
  flightItinerary: FlightItineraryApi
): boolean {
  if (flightItinerary.properties.contract_expiration) {
    return beforeTimestamp(
      flightItinerary.properties.contract_expiration,
      now()
    )
  }
  return false
}

export function buildFlightSearchInput(
  searchInfo: Optional<FlightSearchSummary["search_info"]>,
  trip: Optional<Trip>,
  selectedOption?: FlightSummaryOption
): FlightSearchInput {
  let origin: FlightSearchInput["origin"] = undefined
  let destination: FlightSearchInput["destination"] = undefined

  const tripType =
    selectedOption?.trip_type ?? searchInfo?.trip_type ?? "round-trip"
  const firstSelectedSlice = selectedOption?.flight_slices[0]
  const lastSelectedSlice = selectedOption
    ? last(selectedOption.flight_slices)
    : undefined

  if (tripType !== "multi-city") {
    if (searchInfo?.origin_entity_ref) {
      origin = { entity_ref: searchInfo.origin_entity_ref }
    } else if (searchInfo?.origin_destination_map?.[0]?.[0]) {
      origin = {
        iata: searchInfo.origin_destination_map[0][0],
      }
    } else if (firstSelectedSlice?.departure_airport.entity_ref) {
      origin = { entity_ref: firstSelectedSlice.departure_airport.entity_ref }
    } else if (firstSelectedSlice?.departure_airport.code) {
      origin = {
        iata: firstSelectedSlice.departure_airport.code,
      }
    }

    if (searchInfo?.destination_entity_ref) {
      destination = { entity_ref: searchInfo.destination_entity_ref }
    } else if (searchInfo?.origin_destination_map?.[0]?.[1]) {
      destination = {
        iata: searchInfo.origin_destination_map[0][1],
      }
    } else if (firstSelectedSlice?.arrival_airport.entity_ref) {
      destination = {
        entity_ref: firstSelectedSlice.arrival_airport.entity_ref,
      }
    } else if (firstSelectedSlice?.arrival_airport.code) {
      destination = {
        iata: firstSelectedSlice.arrival_airport.code,
      }
    }
  }

  const tripTravelers = trip && getTripTravelersOrDefault(trip)
  const adultCount = searchInfo?.adults ?? tripTravelers?.adult_count ?? 0
  const childCount = searchInfo?.children ?? tripTravelers?.child_count ?? 0
  const infantInSeatCount = searchInfo?.infants_in_seat ?? 0
  const infantOnLapCount =
    searchInfo?.infants_on_lap ??
    // TODO: remove searchInfo.infants fallback once it is removed from the wire
    searchInfo?.infants ??
    tripTravelers?.infant_count ??
    0
  const tripDates = trip && dayRangeForTimeFrame(trip.time_frame)

  const segments =
    selectedOption?.trip_type === "multi-city"
      ? selectedOption.flight_slices.map((slice, index) => {
          const originalSlice = searchInfo?.slice_details?.[index]
          const departure = departureWallClock(slice)
          return {
            origin_iata:
              originalSlice?.origin_iata ?? slice.departure_airport.code,
            origin_iatas: originalSlice?.origin_iatas,
            origin_ref:
              originalSlice?.origin_ref ??
              (originalSlice?.origin_iata
                ? undefined
                : slice.departure_airport.entity_ref),
            destination_iata:
              originalSlice?.destination_iata ?? slice.arrival_airport.code,
            destination_iatas: originalSlice?.destination_iatas,
            destination_ref:
              originalSlice?.destination_ref ??
              (originalSlice?.destination_iata
                ? undefined
                : slice.arrival_airport.entity_ref),
            date: getDayObject(departure.at, departure.timeZone),
          }
        })
      : searchInfo?.slice_details?.map((sd) => ({
          origin_iata: sd.origin_iata,
          origin_iatas: sd.origin_iatas,
          origin_ref: sd.origin_ref,
          destination_iata: sd.destination_iata,
          destination_iatas: sd.destination_iatas,
          destination_ref: sd.destination_ref,
          date: sd.departure_date,
        }))

  if (tripType === "multi-city" && segments?.[0]) {
    const firstSegment = segments[0]
    origin = firstSegment.origin_ref
      ? { entity_ref: firstSegment.origin_ref }
      : { iata: firstSegment.origin_iata }
    destination = firstSegment.destination_ref
      ? { entity_ref: firstSegment.destination_ref }
      : { iata: firstSegment.destination_iata }
  }

  const selectedDeparture =
    firstSelectedSlice && departureWallClock(firstSelectedSlice)
  const selectedReturnDeparture =
    lastSelectedSlice && departureWallClock(lastSelectedSlice)

  return {
    trip_type: tripType,
    origin: origin,
    destination: destination,
    departure_date:
      selectedDeparture != null
        ? getDayObject(selectedDeparture.at, selectedDeparture.timeZone)
        : searchInfo?.departure_date || tripDates?.from || undefined,
    return_date:
      selectedReturnDeparture != null && selectedOption?.trip_type !== "one-way"
        ? getDayObject(
            selectedReturnDeparture.at,
            selectedReturnDeparture.timeZone
          )
        : searchInfo?.return_date || tripDates?.to || undefined,
    cabin_class: searchInfo?.cabin_class ?? undefined,
    currency: searchInfo?.currency ?? undefined,
    adult_count: adultCount ?? undefined,
    child_count: childCount ?? undefined,
    infant_on_lap_count: infantOnLapCount ?? undefined,
    infant_in_seat_count: infantInSeatCount ?? undefined,
    selected_options:
      selectedOption?.flight_slices.map((slice) => slice.hash_key) ?? undefined,
    segments: segments,
    source: searchInfo?.source,
  }
}

export function buildSearchInputFromState(
  state: FlightSearchSliceState
): FlightSearchInput | undefined {
  if (!state.sheetOpen) {
    return undefined
  }
  const searchOptions = state.sheetSearch.options

  return {
    trip_type: searchOptions.tripType,
    origin: searchOptions.origin?.id
      ? { entity_ref: searchOptions.origin.id }
      : undefined,
    destination: searchOptions.destination?.id
      ? { entity_ref: searchOptions.destination.id }
      : undefined,
    departure_date: searchOptions.range.from,
    return_date: searchOptions.range.to,
    cabin_class: searchOptions.cabin_class,
    adult_count: searchOptions.travelers.adult_count,
    child_count: searchOptions.travelers.child_count,
    infant_on_lap_count: searchOptions.travelers.infant_on_lap_count,
    infant_in_seat_count: searchOptions.travelers.infant_in_seat_count,
    selected_options: state.sheetSearch.selectedOptionKeys,
    segments:
      searchOptions.tripType === "multi-city"
        ? searchOptions.segments?.map((seg) => ({
            origin_iata:
              seg.origin?.type === "airport" ? seg.origin.code : undefined,
            origin_ref: seg.origin?.id,
            destination_iata:
              seg.destination?.type === "airport"
                ? seg.destination.code
                : undefined,
            destination_ref: seg.destination?.id,
            date: seg.date,
          }))
        : undefined,
  }
}

export function buildFlightSheetUrlParams(
  sheetInput: FlightSearchInput
): URLSearchParams {
  const urlParams = new URLSearchParams()

  urlParams.append("trip_type", sheetInput.trip_type)

  if (
    sheetInput.origin &&
    "entity_ref" in sheetInput.origin &&
    sheetInput.origin.entity_ref
  ) {
    urlParams.append("origin_ref", sheetInput.origin.entity_ref)
  }

  if (
    sheetInput.origin &&
    "iata" in sheetInput.origin &&
    sheetInput.origin.iata
  ) {
    urlParams.append("origin_iata", sheetInput.origin.iata)
  }

  if (
    sheetInput.destination &&
    "entity_ref" in sheetInput.destination &&
    sheetInput.destination.entity_ref
  ) {
    urlParams.append("destination_ref", sheetInput.destination.entity_ref)
  }

  if (
    sheetInput.destination &&
    "iata" in sheetInput.destination &&
    sheetInput.destination.iata
  ) {
    urlParams.append("destination_iata", sheetInput.destination.iata)
  }

  if (sheetInput.departure_date) {
    urlParams.append("departure_date", encodeUriJson(sheetInput.departure_date))
  }

  if (sheetInput.return_date) {
    urlParams.append("return_date", encodeUriJson(sheetInput.return_date))
  }

  if (sheetInput.cabin_class) {
    urlParams.append("cabin_class", sheetInput.cabin_class)
  }

  if (sheetInput.currency) {
    urlParams.append("currency", sheetInput.currency)
  }

  if (sheetInput.adult_count) {
    urlParams.append("adult_count", sheetInput.adult_count.toString())
  }

  if (sheetInput.child_count) {
    urlParams.append("child_count", sheetInput.child_count.toString())
  }

  if (sheetInput.infant_on_lap_count) {
    urlParams.append(
      "infant_on_lap_count",
      sheetInput.infant_on_lap_count.toString()
    )
  }

  if (sheetInput.infant_in_seat_count) {
    urlParams.append(
      "infant_in_seat_count",
      sheetInput.infant_in_seat_count.toString()
    )
  }

  if (sheetInput.selected_options && sheetInput.selected_options.length > 0) {
    urlParams.append(
      "selected_options",
      encodeURIComponent(JSON.stringify(sheetInput.selected_options))
    )
  }

  if (
    sheetInput.trip_type === "multi-city" &&
    sheetInput.segments &&
    sheetInput.segments.length > 0
  ) {
    urlParams.append("segments", encodeUriJson(sheetInput.segments))
  }

  if (sheetInput.revise) {
    urlParams.append("revise", "true")
  }

  return urlParams
}

export function buildFlightSearchInputFromItinerary(
  flightItinerary: FlightItineraryApi | FlightItinerarySummary,
  selectOptions = false
): FlightSearchInput {
  // set search options
  const firstSlice = flightItinerary.flight_slices[0]
  const lastSlice = assertExists(last(flightItinerary.flight_slices))
  let cabinClass =
    flightItinerary.properties.slice_ticketing[0].leg_ticketing[0].cabin_class // take the first cabin class

  if (cabinClass === "basic") {
    cabinClass = "economy"
  }

  const departure = departureWallClock(firstSlice)
  const arrival = arrivalWallClock(lastSlice)

  return {
    trip_type: flightItinerary.trip_type,
    origin: firstSlice.departure_airport.entity_ref
      ? { entity_ref: firstSlice.departure_airport.entity_ref }
      : { iata: firstSlice.departure_airport.code },
    destination: firstSlice.arrival_airport.entity_ref
      ? { entity_ref: firstSlice.arrival_airport.entity_ref }
      : { iata: firstSlice.arrival_airport.code },
    cabin_class: cabinClass,
    departure_date: getDayObject(departure.at, departure.timeZone),
    return_date: getDayObject(arrival.at, arrival.timeZone),
    adult_count: flightItinerary.properties.adults,
    child_count: flightItinerary.properties.children,
    infant_on_lap_count: flightItinerary.properties.infants_on_lap ?? 0,
    infant_in_seat_count: flightItinerary.properties.infants_in_seat ?? 0,
    selected_options: selectOptions
      ? flightItinerary.flight_slices.map((x) => x.hash_key)
      : [],
  }
}

export function buildFlightSearchInputFromParams(
  params: URLSearchParams
): FlightSearchInput {
  const tripType = (params.get("trip_type") as FlightTripTypes) ?? "round-trip"
  let origin: FlightSearchInput["origin"] = undefined
  if (params.get("origin_ref")) {
    origin = {
      entity_ref: params.get("origin_ref") as PlaceRef,
    }
  } else if (params.get("origin_iata")) {
    origin = {
      iata: params.get("origin_iata") as string,
    }
  }

  let destination: FlightSearchInput["destination"] = undefined
  if (params.get("destination_ref")) {
    destination = {
      entity_ref: params.get("destination_ref") as PlaceRef,
    }
  } else if (params.get("destination_iata")) {
    destination = {
      iata: params.get("destination_iata") as string,
    }
  }

  return {
    trip_type: tripType,
    origin: origin,
    destination: destination,
    departure_date: params.get("departure_date")
      ? decodeUriJson(params.get("departure_date") as string)
      : undefined,
    return_date: params.get("return_date")
      ? decodeUriJson(params.get("return_date") as string)
      : undefined,
    cabin_class: params.get("cabin_class") as FlightCabinTypes,
    currency: params.get("currency") || undefined,
    adult_count: parseInt(params.get("adult_count") as string) || 0,
    child_count: parseInt(params.get("child_count") as string) || 0,
    infant_on_lap_count:
      parseInt(params.get("infant_on_lap_count") as string) || 0,
    infant_in_seat_count:
      parseInt(params.get("infant_in_seat_count") as string) || 0,
    selected_options: params.get("selected_options")
      ? decodeUriJson(params.get("selected_options") as string)
      : undefined,
    segments:
      tripType === "multi-city" && params.get("segments")
        ? decodeUriJson(params.get("segments") as string)
        : undefined,
    revise: params.get("revise") === "true" || undefined,
    alert_price: parseFloat(params.get("alert_price") as string) || undefined,
  }
}

export function emptyFlightSearchInput(): FlightSearchInput {
  return {
    trip_type: "round-trip",
    origin: undefined,
    destination: undefined,
    departure_date: undefined,
    return_date: undefined,
    cabin_class: undefined,
    adult_count: undefined,
    child_count: undefined,
    infant_on_lap_count: undefined,
    infant_in_seat_count: undefined,
    selected_options: undefined,
  }
}

export async function createEventsForFlightItinerary(
  flightItinerary: FlightItineraryApi,
  target: FlightTripTarget,
  flightApi: FlightApi,
  dispatch: AppDispatch
) {
  const flightItineraryId = assertExists(flightItinerary.id)
  const res =
    "tripRef" in target
      ? await flightApi.addFlightToTrip(target.tripRef, flightItineraryId)
      : await flightApi.addOrUpdateEventsForFlight(
          target.chatId,
          flightItineraryId
        )
  void populateTripCache(dispatch, res.trip)
  dispatch(
    itineraryApi.util.updateQueryData(
      "getItinerary",
      { tripRef: res.trip.ref },
      () => res.itinerary
    )
  )
}

export async function buildFlightSeatMapValue(
  flightBooking: FlightBookingApi,
  flightApi: FlightApi
): Promise<FlightSeatMapValue> {
  let travelers: FlightSeatMapValue["travelers"]
  if (flightBooking.properties.travelers) {
    travelers = flightBooking.properties.travelers.map((t, tix) => {
      return { travelerIx: tix, traveler: t }
    })
  } else {
    const travelerCount =
      flightBooking.flight_itinerary.properties.adults +
      flightBooking.flight_itinerary.properties.children
    travelers = Array.from({ length: travelerCount }, (_, tix) => {
      return { travelerIx: tix, traveler: null }
    })
  }

  const vendorSeatMapId = flightBooking.flight_itinerary.vendor_data.seat_map_id

  if (!vendorSeatMapId) {
    throw new FlightSeatMapUnavailableException("Seat map is not available")
  }

  const res = await flightApi.getFlightSeatMap(vendorSeatMapId)
  const fetchedSeatMaps: FlightSeatMap[] = res.seat_maps

  const seatMapDetails = flightBooking.flight_itinerary.flight_slices.flatMap(
    (fs, fsix) => {
      return fs.flight_legs.map((fl) => {
        const flightLegId = assertExists(fl.id)
        const legTicketing =
          flightBooking.flight_itinerary.properties.slice_ticketing
            .flatMap((st) => st.leg_ticketing)
            .find((x) => fl.id === x.flight_leg_id)

        const fetchMatch = fetchedSeatMaps.find((fsm) => {
          return (
            fsm.flight_info.flight_number === fl.flight_number &&
            fsm.flight_info.airline_iata === fl.airline_iata
          )
        })

        const allAvailableSeats =
          fetchMatch?.decks
            ?.flatMap((d) => d.rows.flatMap((r) => r.seats))
            .filter((s) => s.status === "Available") ?? []
        const allowedForSelectionSeats = allAvailableSeats.filter(
          (s) => !s.paid
        )

        let seatStatus: SeatSelectionStatus = "unknown"
        if (legTicketing?.seat_selection_allowed === false) {
          seatStatus = "not_allowed"
        } else if (allowedForSelectionSeats.length >= travelers.length) {
          seatStatus = "available"
        } else if (allowedForSelectionSeats.length > 0) {
          seatStatus = "partial"
        } else if (allAvailableSeats.length > 0) {
          seatStatus = "paid_only"
        } else if (allAvailableSeats.length === 0) {
          seatStatus = "unavailable"
        } else {
          seatStatus = "no_map"
        }

        return {
          is_return_flight:
            flightBooking.flight_itinerary.trip_type === "round-trip" &&
            fsix === 1,
          seat_selection_status: seatStatus,
          flight_leg_id: flightLegId,
          flight_info: {
            flight_number: fl.flight_number,
            airline_iata: fl.airline_iata,
            aircraft: fetchMatch?.flight_info?.aircraft,
            vendor_seat_map_id: fetchMatch?.flight_info?.vendor_seat_map_id,
          },
          decks: fetchMatch?.decks ?? [],
          selected_seats: [],
        }
      })
    }
  )

  return {
    travelers,
    seatMapDetails,
    flightLegs: flightBooking.flight_itinerary.flight_slices.flatMap(
      (fs) => fs.flight_legs
    ),
  }
}

// map airlines to contract itinerary, in this case the airlines will not be there. Need to map them
// need to remove this after we move airlines into our db
export function mapAirlinesToFlightItinerary(
  itineraryForMap: FlightItineraryApi,
  allAirlines: Airline[]
) {
  itineraryForMap.flight_slices.forEach((slice) => {
    slice.flight_legs.forEach((leg) => {
      if (
        leg.properties.airlines == null ||
        !leg.properties.airlines.find((a) => a.iata === leg.airline_iata)
      ) {
        leg.properties.airlines = uniqBy(
          [
            ...(leg.properties.airlines ?? []),
            ...allAirlines.filter((a) => a.iata === leg.airline_iata),
          ],
          "iata"
        )
      }
    })
  })
}

export function travelerLabel(
  travelerDetail: FlightSeatMapValue["travelers"][0]
) {
  if (travelerDetail.traveler?.first_name) {
    return `${travelerDetail.traveler.first_name} ${travelerDetail.traveler.last_name}`
  }
  return `Traveler ${travelerDetail.travelerIx + 1}`
}

export function sendFlightBookingAnalyticsEvent(
  eventName:
    | "FlightDetailsView"
    | "TripFlightAdd"
    | "TripFlightRemove"
    | "FlightUpgradeSelect"
    | "FlightPriceSummaryOpen"
    | "FlightPriceSummaryClose"
    | "FlightBookingCreate",
  contractItinerary: FlightItineraryApi,
  itineraryEvent?: ItineraryEventWithMindtripFlightBooking,
  upgrade?: FlightContractUpgrade
) {
  switch (eventName) {
    case "FlightDetailsView":
    case "FlightPriceSummaryOpen":
    case "FlightPriceSummaryClose":
    case "FlightBookingCreate":
      if (contractItinerary) {
        return sendAnalyticsEvent({
          name: eventName,
          properties: {
            flight: {
              ...flightItineraryApiToAmplitudeProperties(contractItinerary),
            },
          },
        })
      }
      break
    case "TripFlightAdd":
    case "TripFlightRemove":
      if (itineraryEvent) {
        return sendAnalyticsEvent({
          name: eventName,
          properties: {
            ...itineraryEventToAmplitudeProperties(itineraryEvent),
          },
        })
      }
      break
    case "FlightUpgradeSelect":
      if (contractItinerary && upgrade) {
        return sendAnalyticsEvent({
          name: eventName,
          properties: {
            flight: {
              ...flightItineraryApiToAmplitudeProperties(contractItinerary),
              ...flightUpgradeToAmplitudeProperties(upgrade),
            },
          },
        })
      }
      break
    default:
      return { promise: Promise.resolve("unhandled event") }
  }
}

export function initSearchValues(
  searchInput: FlightSearchInput
): FlightSearchOptions {
  const travelers = {
    adult_count: searchInput?.adult_count || 0,
    child_count: searchInput?.child_count || 0,
    infant_on_lap_count: searchInput?.infant_on_lap_count || 0,
    infant_in_seat_count: searchInput?.infant_in_seat_count || 0,
  }

  // default to 1 adult if no travelers are specified
  if (
    travelers.adult_count === 0 &&
    travelers.child_count === 0 &&
    travelers.infant_on_lap_count === 0 &&
    travelers.infant_in_seat_count === 0
  ) {
    travelers.adult_count = 1
  }

  // make sure this matches the defaults from flight_knowledge_base.rb
  const departureDate = searchInput?.departure_date
    ? searchInput.departure_date
    : undefined
  const returnDate = searchInput?.return_date
    ? searchInput.return_date
    : undefined

  return {
    tripType: searchInput?.trip_type || "round-trip",
    cabin_class: searchInput?.cabin_class || "economy",
    currency: searchInput?.currency,
    origin: undefined,
    destination: undefined,
    range: {
      from: departureDate,
      to: returnDate,
    },
    travelers,
    segments: searchInput?.segments?.map((seg) => ({
      origin: undefined,
      destination: undefined,
      date: seg.date,
    })),
  }
}

export function buildUpdatedFilters(
  existingFilters: FlightSearchFilters,
  updatedOptions: FlightOption[],
  baseDay: DayObject,
  airlines: Airline[]
): FlightSearchFilters {
  const minPrice = Math.min(...updatedOptions.map((x) => x.price.total))
  const maxPrice = Math.max(...updatedOptions.map((x) => x.price.total))

  const minDuration = Math.min(
    ...updatedOptions.map((x) =>
      Math.floor(x.flight_slice.properties.duration / 60)
    )
  )

  const maxDuration = Math.max(
    ...updatedOptions.map((x) =>
      Math.ceil(x.flight_slice.properties.duration / 60)
    )
  )

  const minDeparture = Math.min(
    ...updatedOptions.map((x) => {
      const baseTimeStamp = getTimestamp(
        baseDay,
        START_OF_DAY,
        x.flight_slice.departure_airport.time_zone
      )

      return Math.floor(
        hoursBetweenDecimal(baseTimeStamp, x.flight_slice.departs_at)
      )
    })
  )

  const maxDeparture = Math.max(
    ...updatedOptions.map((x) => {
      const baseTimeStamp = getTimestamp(
        baseDay,
        START_OF_DAY,
        x.flight_slice.departure_airport.time_zone
      )
      return Math.ceil(
        hoursBetweenDecimal(baseTimeStamp, x.flight_slice.departs_at)
      )
    })
  )

  const minArrival = Math.min(
    ...updatedOptions.map((x) => {
      const baseTimeStamp = getTimestamp(
        baseDay,
        START_OF_DAY,
        x.flight_slice.arrival_airport.time_zone
      )
      return Math.floor(
        hoursBetweenDecimal(baseTimeStamp, x.flight_slice.arrives_at)
      )
    })
  )

  const maxArrival = Math.max(
    ...updatedOptions.map((x) => {
      const baseTimeStamp = getTimestamp(
        baseDay,
        START_OF_DAY,
        x.flight_slice.arrival_airport.time_zone
      )
      return Math.ceil(
        hoursBetweenDecimal(baseTimeStamp, x.flight_slice.arrives_at)
      )
    })
  )

  const airports = uniqBy(
    updatedOptions.flatMap((x) => [
      x.flight_slice.departure_airport,
      x.flight_slice.arrival_airport,
    ]),
    "code"
  )

  const layoverAirports = uniqBy(
    updatedOptions.flatMap((x) =>
      x.flight_slice.layovers.map((x) => x.airport)
    ),
    "code"
  )

  return {
    values: {
      ...existingFilters.values,
      airlines: airlines,
      airports: airports,
      layoverAirports: layoverAirports,
      allAirlines: true,
      allAirports: true,
      price: maxPrice,
      duration: maxDuration,
      departureTimes: [minDeparture, maxDeparture],
      arrivalTimes: [minArrival, maxArrival],
    },
    options: {
      ...existingFilters.options,
      airlines: airlines,
      airports: airports,
      layoverAirports: layoverAirports,
      alliances: [],
      priceRange: [minPrice, maxPrice],
      durationRange: [minDuration, maxDuration],
      date: baseDay,
      departureRange: [minDeparture, maxDeparture],
      arrivalRange: [minArrival, maxArrival],
    },
  }
}

export function buildFlightOptions(
  apiOptions: FlightOption[],
  flightItineraryEvents: ItineraryEventWithMindtripFlightBooking[]
) {
  const bestOption = orderBy(
    apiOptions,
    ["score", "price.total"],
    ["desc", "asc"]
  )[0]
  const cheapestPrice = sortBy(apiOptions.map((x) => x.price.total))[0]
  const fastestDuration = sortBy(
    apiOptions.map((x) => x.flight_slice.properties.duration)
  )[0]

  return apiOptions.map((flightOption) => {
    const flightSlice = flightOption.flight_slice
    return {
      ...flightOption,
      upgrades: flightOption.upgrades,
      best: flightOption === bestOption,
      cheapest: flightOption.price.total === cheapestPrice,
      fastest: flightSlice.properties.duration === fastestDuration,
      addedToTrip: flightItineraryEvents
        .flatMap((x) => x.flight_itinerary?.flight_slices || [])
        .some((fs) => flightSlice.id === fs.id),
    }
  })
}

const FlightSearchSchema = z
  .object({
    tripType: z.string(),
    origin: z
      .object({
        code: z.string(),
      })
      .or(z.object({ id: z.string() })),
    destination: z
      .object({
        code: z.string(),
      })
      .or(z.object({ id: z.string() })),
    range: z.object({
      from: z
        .object({
          year: z.number(),
          month: z.number(),
          day: z.number(),
        })
        .refine((from) => {
          const current = today(browserTimeZone())
          return !beforeDayObject(from, current)
        }, "Search date cannot be in the past"),
      to: z
        .object({
          year: z.number(),
          month: z.number(),
          day: z.number(),
        })
        .optional(),
    }),
    cabin_class: z.string(),
  })
  .superRefine(({ range }, ctx) => {
    if (range?.from != null && range?.to != null) {
      if (beforeDayObject(range.to, range.from)) {
        ctx.addIssue({
          code: "custom",
          message: "Return date cannot be before departure date",
          path: ["range", "to"],
        })
      }
    }
  })

export type FlightSearchSchemaErrors = z.core.$ZodFormattedError<
  z.output<typeof FlightSearchSchema>
>

export type ValidatedSearchOptions = Omit<
  NonNullableFields<FlightSearchOptions>,
  "range" | "segments"
> & {
  range: {
    to: DayObject | undefined
    from: DayObject
  }
  segments?: ValidatedMultiCitySegment[]
}

export type ValidationResult<TValue, TError> =
  | {
      valid: true
      errors: undefined
      value: TValue
    }
  | {
      valid: false
      errors: TError | undefined
      value: null
    }

export function validateSearchOptions(
  searchOptions: FlightSearchOptions
): ValidationResult<ValidatedSearchOptions, FlightSearchSchemaErrors> {
  if (searchOptions.tripType === "multi-city") {
    const segments = searchOptions.segments
    if (!segments || segments.length < 2) {
      return { valid: false, errors: undefined, value: null }
    }

    for (const seg of segments) {
      if (!seg.origin || !seg.destination || !seg.date) {
        return { valid: false, errors: undefined, value: null }
      }
    }

    return {
      valid: true,
      errors: undefined,
      value: {
        ...searchOptions,
        segments: segments as ValidatedMultiCitySegment[],
      } as ValidatedSearchOptions,
    }
  }

  const res = FlightSearchSchema.safeParse(searchOptions)

  if (res.success) {
    return {
      valid: true,
      errors: undefined,
      value: searchOptions as ValidatedSearchOptions,
    }
  } else {
    return {
      valid: false,
      errors: z.formatError(res.error),
      value: null,
    }
  }
}

export function validateMultiCityDates(
  validated: ValidatedSearchOptions
): string | null {
  if (validated.tripType !== "multi-city" || !validated.segments?.length)
    return null
  for (let i = 1; i < validated.segments.length; i++) {
    const prev = validated.segments[i - 1]?.date
    const curr = validated.segments[i]?.date
    if (prev != null && curr != null && beforeDayObject(curr, prev)) {
      return `Leg ${i + 1} departure date cannot be before leg ${i} departure date`
    }
  }
  return null
}

function getAirportIatas(tp: FlightTransitPoint): string[] {
  if (tp.type === "airport") return [tp.code]

  return tp.nearby_airports.map((na) => na.airport_transit_point.airport.code)
}

function buildSabreSliceParams(
  origin: FlightTransitPoint,
  destination: FlightTransitPoint,
  departureDate: DayObject
): FlightSliceParams {
  const sliceParams: FlightSliceParams = {
    departure_date: departureDate,
  }

  if (origin.type === "geo") {
    sliceParams.origin_ref = origin.id
  } else {
    sliceParams.origin_iata = origin.code
  }

  if (destination.type === "geo") {
    sliceParams.destination_ref = destination.id
  } else {
    sliceParams.destination_iata = destination.code
  }

  return sliceParams
}

export function buildSearchParams(
  validated: ValidatedSearchOptions,
  flightProvider: "sabre" | "priceline" = "sabre"
): FlightSearchParams[] {
  const sabreFlightSearchEnabled = flightProvider !== "priceline"

  // Multi-city: bundle all segments into a single search request.
  // Sabre does not support checkout multi-tickets for v1, we will do this for v2.
  if (validated.tripType === "multi-city" && validated.segments?.length) {
    const sliceDetails: FlightSliceParams[] = validated.segments.map((seg) => {
      if (sabreFlightSearchEnabled) {
        return buildSabreSliceParams(seg.origin, seg.destination, seg.date)
      }

      return {
        origin_iata: getAirportIatas(seg.origin)[0],
        destination_iata: getAirportIatas(seg.destination)[0],
        departure_date: seg.date,
      }
    })

    const baseParams = {
      slice_details: sliceDetails,
      cabin_class: validated.cabin_class,
      currency: validated.currency,
      adults: validated.travelers.adult_count,
      children: validated.travelers.child_count,
      infants_on_lap: validated.travelers.infant_on_lap_count,
      infants_in_seat: validated.travelers.infant_in_seat_count,
    }

    if (sabreFlightSearchEnabled) {
      return [0, 1, 2].map((maxConnections) => ({
        ...baseParams,
        max_connections: maxConnections,
      }))
    }

    return [baseParams]
  }

  if (sabreFlightSearchEnabled) {
    let sliceDetails: FlightSliceParams[] = []

    if (validated.tripType === "one-way") {
      sliceDetails = [
        buildSabreSliceParams(
          validated.origin,
          validated.destination,
          validated.range.from
        ),
      ]
    } else if (validated.tripType === "round-trip") {
      sliceDetails = [
        buildSabreSliceParams(
          validated.origin,
          validated.destination,
          validated.range.from
        ),
        buildSabreSliceParams(
          validated.destination,
          validated.origin,
          assertExists(validated.range.to)
        ),
      ]
    }

    return [0, 1, 2].map((maxConnections) => ({
      slice_details: sliceDetails,
      cabin_class: validated.cabin_class,
      currency: validated.currency,
      adults: validated.travelers.adult_count,
      children: validated.travelers.child_count,
      infants_on_lap: validated.travelers.infant_on_lap_count,
      infants_in_seat: validated.travelers.infant_in_seat_count,
      max_connections: maxConnections,
    }))
  }

  const originIatas = getAirportIatas(validated.origin)
  const destinationIatas = getAirportIatas(validated.destination)

  return product(originIatas, destinationIatas).flatMap(
    ([originIata, destinationIata]) => {
      let sliceDetails: FlightSliceParams[] = []

      if (validated.tripType === "one-way") {
        sliceDetails = [
          {
            origin_iata: originIata,
            destination_iata: destinationIata,
            departure_date: validated.range.from,
          },
        ]
      } else if (validated.tripType === "round-trip") {
        sliceDetails = [
          {
            origin_iata: originIata,
            destination_iata: destinationIata,
            departure_date: validated.range.from,
          },
          {
            origin_iata: destinationIata,
            destination_iata: originIata,
            departure_date: assertExists(validated.range.to),
          },
        ]
      }

      return [
        {
          slice_details: sliceDetails,
          cabin_class: validated.cabin_class,
          currency: validated.currency,
          adults: validated.travelers.adult_count,
          children: validated.travelers.child_count,
          infants_on_lap: validated.travelers.infant_on_lap_count,
          infants_in_seat: validated.travelers.infant_in_seat_count,
        },
      ]
    }
  )
}

export async function saveFlightItinerary(
  flightItinerary: FlightItineraryApi,
  airlines: Airline[],
  flightApi: FlightApi,
  apiLogsIds?: string[],
  selectedOption?: FlightOption | FlightOptionPayload,
  selectedUpgrade?: FlightContractUpgrade
): Promise<FlightItineraryApi> {
  // For Sabre flights, send full flight data instead of just contract_id
  // This avoids issues with contract cache expiration
  const isSabre = flightItinerary.vendor === "sabre"

  const saveReq: SaveFlightItinerary = isSabre
    ? {
        flight_data: selectedOption,
        selected_upgrade: selectedUpgrade,
        airlines,
        favorite: flightItinerary.favorite,
        api_log_ids: apiLogsIds,
      }
    : {
        contract_id: assertExists(flightItinerary.vendor_data.contract_id),
        airlines,
        favorite: flightItinerary.favorite,
        api_log_ids: apiLogsIds,
      }

  if (!flightItinerary.id) {
    return await flightApi.createFlightItinerary(saveReq)
  } else {
    return await flightApi.updateFlightItinerary(flightItinerary.id, saveReq)
  }
}

export function displayAirportCity(airport: AirportSummary) {
  return airport.metro_geo?.name ?? airport.city
}

export function flightBookingTravelers(
  flightBooking: FlightBookingApi
): FlightTravelers {
  const adult_count = flightBooking.flight_itinerary.properties.adults
  const child_count = flightBooking.flight_itinerary.properties.children
  const infant_on_lap_count =
    flightBooking.properties.travelers?.filter((x) => x.lap_infant).length ?? 0
  const infant_in_seat_count = 0 // TODO: track infants in seat in booking
  return {
    adult_count,
    child_count,
    infant_on_lap_count,
    infant_in_seat_count,
  }
}

export function buildSheetUrlFromItinerary(
  flightItinerary: FlightItineraryApi | FlightItinerarySummary
) {
  const sheetInput = buildFlightSearchInputFromItinerary(flightItinerary, true)
  const urlParams = buildFlightSheetUrlParams(sheetInput)
  urlParams.set("view", "flight-search")

  return routes.chat.href({
    id: flightItinerary.latest_chat_id
      ? [String(flightItinerary.latest_chat_id)]
      : undefined,
    query: urlParams,
  })
}

export function formatFlightNumber(event: ItineraryEvent) {
  const props = event.properties
  return props.airline_iata && props.flight_number
    ? `${props.airline_iata} ${props.flight_number}`
    : undefined
}

export function formatFlightTitle(event: ItineraryEvent) {
  if (event.type !== "flight") return undefined
  if (event.place?.type !== "airport" && event.end_place?.type !== "airport") {
    return event.title
  }

  const parts: string[] = []
  if (event.place?.type === "airport") {
    parts.push(`${displayAirportCity(event.place)} (${event.place.code})`)
  }
  if (event.end_place?.type === "airport") {
    parts.push(
      `${displayAirportCity(event.end_place)} (${event.end_place.code})`
    )
  }
  return parts.join(" - ")
}

export function formatFlightTitleCompact(event: ItineraryEvent) {
  if (event.type !== "flight") return undefined
  const airportParts: string[] = []
  if (event.place?.type === "airport") {
    airportParts.push(event.place.code)
  }
  if (event.end_place?.type === "airport") {
    airportParts.push(event.end_place.code)
  }
  const airportString = airportParts.join("-")
  const parts: string[] = []
  if (airportString) {
    parts.push(airportString)
  }
  const flightNumber = formatFlightNumber(event)
  if (flightNumber) {
    parts.push(flightNumber)
  }

  return parts.length === 0 ? event.title : parts.join(" · ")
}

export function formatFlightTitleIATACodes(leg: FlightLegApi) {
  return `${leg.departure_airport.code} - ${leg.arrival_airport.code}`
}

export function formatFlightEventDuration(
  locale: string,
  event: ItineraryEvent
) {
  return formatFlightDuration(
    locale,
    durationInMinutes(event.start_at, event.end_at)
  )
}

export function formatFlightDepartureTime(
  locale: string,
  event: ItineraryEvent
) {
  return formatHourMinuteZone(locale, event.start_at, event.time_zone)
}

export function formatFlightArrivalTime(locale: string, event: ItineraryEvent) {
  const dayDifference = nightsBetweenDayObjects(
    getDayObject(event.start_at, event.time_zone),
    getDayObject(event.end_at, event.end_time_zone)
  )
  const timeSuffix = formatDayBoundarySuffix(locale, dayDifference)
  const arrival = `${formatHourMinute(locale, event.end_at, event.end_time_zone)} ${formatTimeZone(locale, event.end_time_zone)}`
  return timeSuffix ? `${arrival} ${timeSuffix}` : arrival
}

export function formatFlightEventCodes(event: ItineraryEvent) {
  if (
    event.type !== "flight" ||
    event.place?.type !== "airport" ||
    event.end_place?.type !== "airport"
  ) {
    return undefined
  }
  const parts: string[] = []
  if (event.place) {
    parts.push(event.place.code)
  }
  if (event.end_place) {
    parts.push(event.end_place.code)
  }
  return parts.join(" – ")
}

/**
 * Convert a single FlightLegApi to a FlightSliceApi-compatible object.
 * Useful for displaying a single flight leg using components that expect a slice.
 */
export function flightLegToSlice(leg: FlightLegApi) {
  return {
    id: null,
    departure_airport_id: leg.departure_airport_id,
    departure_airport: leg.departure_airport,
    arrival_airport_id: leg.arrival_airport_id,
    arrival_airport: leg.arrival_airport,
    departs_at: leg.departs_at,
    arrives_at: leg.arrives_at,
    stops: 0,
    hash_key: leg.hash_key,
    properties: {
      duration: leg.duration,
      notes: leg.properties.notes,
      departure_local_date: leg.properties.departure_local_date,
      departure_local_time: leg.properties.departure_local_time,
      arrival_local_date: leg.properties.arrival_local_date,
      arrival_local_time: leg.properties.arrival_local_time,
    },
    layovers: [],
    flight_itinerary_id: 0,
    created_at: null,
    updated_at: null,
    flight_legs: [leg],
  }
}

export interface FlightWallClock {
  at: Timestamp
  timeZone: string
}

interface TimedFlight {
  departs_at: Timestamp
  arrives_at: Timestamp
  departure_airport: AirportSummary
  arrival_airport: AirportSummary
  properties: FlightLocalTimes
}

function flightWallClock(
  localDate: DayObject | undefined,
  localTime: string | undefined,
  at: Timestamp,
  timeZone: string
): FlightWallClock {
  if (localDate == null || localTime == null) {
    return { at, timeZone }
  }
  const [hour, minute] = localTime.split(":").map(Number)
  return {
    at: getTimestamp(localDate, { hour, minute }, "UTC"),
    timeZone: "UTC",
  }
}

export function departureWallClock(flight: TimedFlight): FlightWallClock {
  return flightWallClock(
    flight.properties.departure_local_date,
    flight.properties.departure_local_time,
    flight.departs_at,
    flight.departure_airport.time_zone
  )
}

export function arrivalWallClock(flight: TimedFlight): FlightWallClock {
  return flightWallClock(
    flight.properties.arrival_local_date,
    flight.properties.arrival_local_time,
    flight.arrives_at,
    flight.arrival_airport.time_zone
  )
}
